import type { ReactNode } from "react";
import { Link } from "wouter";

/* Night auth screens (Figma 01 Sign in, 02 Create account, 03 Reset password).
   Used by the login page and the set-new-password page. */
export const AUTH_STYLES = {
  card: "w-full max-w-[448px] border-foreground/8 bg-white shadow-[0_30px_90px_rgba(110,83,172,0.5),0_2px_6px_rgba(11,10,34,0.35)]",
  header: "items-center space-y-0 p-6 text-center",
  badge:
    "mb-2 h-16 w-16 rounded-full shadow-[0_2px_4px_-2px_rgba(47,41,37,0.1),0_4px_6px_-1px_rgba(47,41,37,0.1)]",
  title: "font-display text-2xl font-semibold leading-8 tracking-[-0.6px] text-foreground",
  subtitle: "mt-1 text-sm text-muted-foreground",
  field: "space-y-[4.5px] pt-[5.5px]",
  input: "bg-white shadow-[0_1px_2px_rgba(47,41,37,0.1),0_1px_3px_rgba(47,41,37,0.1)]",
  primary: "w-full text-base text-white",
  secondary:
    "flex h-[42px] w-full items-center justify-center gap-3 rounded-md border border-foreground/10 bg-white px-4 text-sm font-medium text-foreground shadow-[0_1px_1px_rgba(47,41,37,0.1),0_1px_1.5px_rgba(47,41,37,0.1)] transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
  footerLink: "text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline",
} as const;

/** Full-bleed night backdrop: blurred hands-galaxy art, a night scrim and a violet glow. */
export function AuthBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-night">
      <img
        src="/images/landing/v6/hands-galaxy-720.webp"
        alt=""
        decoding="async"
        className="absolute left-[-29.17%] top-[-4.33%] h-[110.44%] w-[158.33%] max-w-none object-cover blur-[65px]"
      />
      <div className="absolute inset-0 bg-night/26" />
      <div className="absolute inset-0 bg-[radial-gradient(520px_420px_at_50%_50.9%,rgba(122,95,208,0.32),rgba(122,95,208,0))]" />
    </div>
  );
}

export function AuthShell({
  backHref,
  backLabel,
  children,
}: {
  backHref: string;
  backLabel: string;
  children: ReactNode;
}) {
  return (
    <div className="relative isolate flex min-h-screen flex-col bg-night">
      <AuthBackdrop />
      <header className="w-full px-6 pb-[18px] pt-[22px] sm:px-10">
        <Link href={backHref} className="text-sm text-starlight/86 transition-colors hover:text-starlight">
          {backLabel}
        </Link>
      </header>
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-11">{children}</div>
    </div>
  );
}
